// Project only the displayed columns. Raw parser rows remain untouched and are
// released after preparation instead of retaining several full-width copies.
const cell = value => value === null || value === undefined ? '' : String(value)
const type = value => !value ? 'empty' : /^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}(?::\d{2})?)?/.test(value) ? 'date' :
  /^-?\d+(?:\.\d+)?$/.test(value) ? 'numeric' : /^[A-Za-z]+$/.test(value) ? 'text' : 'mixed'

// Post one task per yield without accumulating nested timers. Each call owns
// its ports/timer and releases them on completion or navigation cancellation.
function yieldCsvPreview(signal) {
  return new Promise((resolve, reject) => {
    let channel, timer, settled = false
    const finish = error => {
      if (settled) return
      settled = true
      signal?.removeEventListener('abort', cancel)
      if (timer !== undefined) clearTimeout(timer)
      if (channel) {
        channel.port1.onmessage = null
        channel.port1.onmessageerror = null
        channel.port1.close()
        channel.port2.close()
      }
      error ? reject(error) : resolve()
    }
    const cancel = () => finish(new DOMException('Preview cancelled', 'AbortError'))
    if (signal?.aborted) { cancel(); return }
    signal?.addEventListener('abort', cancel, { once: true })
    try {
      if (typeof MessageChannel === 'function') {
        channel = new MessageChannel()
        channel.port1.onmessage = () => finish()
        channel.port1.onmessageerror = () => finish(new Error('Unable to schedule spreadsheet preview'))
        channel.port2.postMessage(null)
      } else timer = setTimeout(() => finish(), 0)
    } catch (error) { finish(error) }
    if (signal?.aborted) cancel()
  })
}

// Papa's string parser is synchronous unless its chunk callback pauses it.
// Yield between chunks so sheet changes/navigation can abort parsing itself,
// before projection starts. Keep one bounded prefix across all parser chunks.
export async function parseCsvPreview(text, parse, { maxRows = 10000, signal,
  chunkSize = 256 * 1024, yieldControl = () => yieldCsvPreview(signal) } = {}) {
  if (typeof text !== 'string' || typeof parse !== 'function') throw new TypeError('CSV parsing requires text and a parser')
  if (![maxRows, chunkSize].every(value => Number.isSafeInteger(value) && value > 0) || !Number.isSafeInteger(maxRows + 2)) {
    throw new RangeError('CSV parsing limits must be positive integers')
  }
  const aborted = () => new DOMException('Preview cancelled', 'AbortError')
  if (signal?.aborted) throw aborted()
  await new Promise((resolve, reject) => {
    let settled = false
    const finish = error => {
      if (settled) return
      settled = true
      signal?.removeEventListener('abort', cancel)
      error ? reject(error) : resolve()
    }
    const cancel = () => finish(aborted())
    signal?.addEventListener('abort', cancel, { once: true })
    Promise.resolve().then(yieldControl).then(() => finish()).catch(finish)
  })
  if (signal?.aborted) throw aborted()
  // Detect the dialect from a bounded row sample of the complete input, rather
  // than guessing from an incomplete first chunk (notably for semicolon CSV).
  const dialect = parse(text, { header: false, skipEmptyLines: true, dynamicTyping: false, preview: 10 })
  const inputLength = text.charCodeAt(0) === 0xFEFF ? text.length - 1 : text.length
  return new Promise((resolve, reject) => {
    const data = [], errors = dialect.errors.filter(error => error.type === 'Delimiter')
    let handle, meta = {}, settled = false, errorCount = errors.length
    const finish = (error) => {
      if (settled) return
      settled = true
      signal?.removeEventListener('abort', cancel)
      if (error) { data.length = 0; errors.length = 0; reject(error) }
      else resolve({ data, errors, errorCount, meta })
    }
    const cancel = () => { finish(aborted()); handle?.abort() }
    signal?.addEventListener('abort', cancel, { once: true })
    try {
      parse(text, {
        header: false, skipEmptyLines: true, dynamicTyping: false, chunkSize,
        delimiter: dialect.meta.delimiter, newline: dialect.meta.linebreak,
        // Papa's preview counts raw records before skipEmptyLines. Enforce the
        // retained-row ceiling here so blank lines cannot hide valid records.
        chunk(results, parser) {
          handle = parser
          if (settled || signal?.aborted) { cancel(); return }
          const remaining = maxRows + 2 - data.length
          for (const row of results.data.slice(0, remaining)) data.push(row)
          errorCount += results.errors.length
          for (const error of results.errors.slice(0, Math.max(0, 100 - errors.length))) errors.push(error)
          meta = { ...results.meta }
          if (data.length >= maxRows + 2) {
            meta.truncated ||= results.data.length > remaining || meta.cursor < inputLength
            finish(); parser.abort(); return
          }
          parser.pause()
          Promise.resolve().then(yieldControl).then(() => {
            if (settled || signal?.aborted) { cancel(); return }
            parser.resume()
          }).catch(error => { finish(error); parser.abort() })
        },
        complete() { finish() },
        error(error) { finish(error) }
      })
    } catch (error) { finish(error); handle?.abort() }
  })
}

async function hasHeader(rows, budget) {
  if (rows.length < 2) return false
  const sampleRows = rows.slice(1, 11)
  let comparable = 0, same = 0, nonEmpty = 0, numeric = 0, labels = 0
  for (let index = 0; index < rows[0].length; index++) {
    const first = cell(rows[0][index]).trim()
    if (++budget.work >= budget.yieldEvery) await budget.pause()
    // Formula text is spreadsheet data. Never drop its first row as a label.
    if (first.startsWith('=')) return false
    if (!first) continue
    nonEmpty++
    const firstType = type(first)
    if (firstType === 'numeric' || firstType === 'date') numeric++
    if (/[A-Za-z]/.test(first) && /[\s_-]/.test(first)) labels++
    const counts = new Map()
    for (const row of sampleRows) {
      const valueType = type(cell(row[index]).trim())
      if (++budget.work >= budget.yieldEvery) await budget.pause()
      if (valueType !== 'empty') counts.set(valueType, (counts.get(valueType) || 0) + 1)
    }
    if (!counts.size) continue
    comparable++
    const dominant = [...counts].sort((a, b) => b[1] - a[1])[0][0]
    if (dominant === firstType) same++
  }
  if (!nonEmpty || !comparable) return true
  if (labels / nonEmpty >= 0.6 && same / comparable <= 0.5) return true
  if (same / comparable >= 0.7 && numeric / nonEmpty >= 0.4) return false
  if (same / comparable >= 0.85 && labels / nonEmpty < 0.4) return false
  return true
}

async function fieldNames(headers, count, budget) {
  const used = new Set(['_csvRow'])
  const suffixes = new Map()
  const fields = []
  for (let index = 0; index < count; index++) {
    const base = cell(headers?.[index]).trim() || `Column ${index + 1}`
    let name = base, suffix = suffixes.get(base) || 2
    while (used.has(name)) name = `${base} (${suffix++})`
    suffixes.set(base, suffix)
    used.add(name)
    fields.push(name)
    if (++budget.work >= budget.yieldEvery) await budget.pause()
  }
  return fields
}

export async function prepareCsvPreview(rows, { maxColumns = 100, maxRows = 10000, maxCells = 500000,
  signal, yieldControl = () => yieldCsvPreview(signal), yieldEvery = 20000 } = {}) {
  if (![maxColumns, maxRows, maxCells, yieldEvery].every(value => Number.isSafeInteger(value) && value > 0)) {
    throw new RangeError('CSV preview limits must be positive integers')
  }
  const check = () => { if (signal?.aborted) throw new DOMException('Preview cancelled', 'AbortError') }
  check()
  const budget = { work: 0, yieldEvery, pause: async () => { await yieldControl(); check(); budget.work = 0 } }
  const sample = rows.slice(0, maxRows + 2)
  const originalColumnCount = sample.reduce((max, row) => Math.max(max, row.length), 0)
  const header = await hasHeader(sample, budget)
  check()
  const fields = await fieldNames(header ? sample[0] : null, originalColumnCount, budget)
  check()
  const offset = header ? 1 : 0
  const nonEmpty = new Uint8Array(originalColumnCount), populated = new Uint8Array(sample.length)
  let nonEmptyCount = 0
  // Keep source column order even when a ragged row supplies a column later.
  for (let index = offset; index < sample.length; index++) {
    const row = sample[index]
    for (let column = 0; column < row.length; column++) {
      if (row[column] !== '' && row[column] !== null && row[column] !== undefined) {
        if (!nonEmpty[column]) { nonEmpty[column] = 1; nonEmptyCount++ }
        populated[index] = 1
      }
      if (++budget.work >= yieldEvery) await budget.pause()
    }
  }
  const indexes = []
  const columnLimit = Math.min(maxColumns, maxCells)
  for (let index = 0; index < nonEmpty.length && indexes.length < columnLimit; index++) {
    if (nonEmpty[index]) indexes.push(index)
  }
  const columnsToKeep = indexes.map(index => fields[index])
  const rowLimit = Math.min(maxRows, Math.max(1, Math.floor(maxCells / Math.max(1, indexes.length))))
  const data = []
  let limitedRows = false
  for (let index = offset; index < sample.length; index++) {
    if (!populated[index]) continue
    if (data.length >= rowLimit) { limitedRows = true; break }
    const row = { _csvRow: index + 1 }
    for (let column = 0; column < indexes.length; column++) {
      const key = columnsToKeep[column], value = cell(sample[index][indexes[column]])
      if (key === '__proto__') Object.defineProperty(row, key, { value, enumerable: true, writable: true, configurable: true })
      else row[key] = value
      if (++budget.work >= yieldEvery) await budget.pause()
    }
    data.push(row)
  }
  check()
  return { data, columnsToKeep, originalColumnCount, truncated: nonEmptyCount > columnLimit,
    headerlessDetected: !header, limitedRows: limitedRows || rows.length > sample.length,
    cellCount: data.length * indexes.length }
}

export async function readCsvPreview(response, maxBytes, signal) {
  const check = () => { if (signal?.aborted) throw new DOMException('Preview cancelled', 'AbortError') }
  check()
  const tooLarge = () => new Error(`Spreadsheet exceeds the ${Math.floor(maxBytes / 1024 / 1024)} MiB preview limit. Download the original to view all data.`)
  // Request cleanup without awaiting an upstream cancel implementation, which
  // may never settle. Keep the size/error result independent of that cleanup.
  const cancel = stream => { stream?.cancel().catch(() => { /* An aborted stream may already be errored. */ }) }
  if (Number(response.headers?.get('content-length')) > maxBytes) {
    cancel(response.body)
    throw tooLarge()
  }
  const reader = response.body?.getReader()
  if (!reader) {
    const text = await response.text()
    check()
    if (new TextEncoder().encode(text).byteLength > maxBytes) throw tooLarge()
    return text
  }
  const decoder = new TextDecoder(), chunks = []
  let bytes = 0
  // Fetch normally errors its stream on abort. Explicitly cancelling also
  // releases a stalled reader, including adapters with an independent body.
  const abortRead = () => cancel(reader)
  signal?.addEventListener('abort', abortRead, { once: true })
  try {
    while (true) {
      check()
      const { value, done } = await reader.read()
      check()
      if (done) break
      bytes += value.byteLength
      if (bytes > maxBytes) { cancel(reader); throw tooLarge() }
      chunks.push(decoder.decode(value, { stream: true }))
    }
    chunks.push(decoder.decode())
    return chunks.join('')
  } finally {
    signal?.removeEventListener('abort', abortRead)
    reader.releaseLock()
  }
}

// The columns that hold figures (amounts, quantities, percentages), so the
// viewer can set them right and their digits line up. A figure has a decimal
// point, a thousands comma, a currency sign or a percent, or is a short whole
// number; a long run of digits or one with a leading zero is an identifier
// (a part, item or account number) and stays left. A column counts when nearly
// every filled cell in the first rows is a figure.
const FIGURE = /^\(?[-+]?[$€£¥]?\s?(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?\)?%?$/
export function figureColumnsIn(rows, keys, { sample = 200, share = 0.9 } = {}) {
  const figures = new Set()
  const sampled = rows.slice(0, sample)
  for (const key of keys) {
    let filled = 0
    let matched = 0
    for (const row of sampled) {
      const value = cell(row[key]).trim()
      if (!value) continue
      filled += 1
      const isId = /^0\d/.test(value) || /^\d{7,}$/.test(value)
      if (!isId && FIGURE.test(value)) matched += 1
    }
    if (filled > 0 && matched / filled >= share) figures.add(key)
  }
  return figures
}
